import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/cn";
import { formatMoney } from "@/lib/format";
import { inventoryRepository } from "@/repositories/catalogueRepository";
import { salesRepository } from "@/repositories/salesRepository";
import { useAuthStore } from "@/stores/authStore";

type Kind = "today" | "stock";

interface Row {
  label: string;
  /** `null` renders an em dash. No fabricated figures — see the note below. */
  value: string | null;
  tone?: "default" | "brand" | "warn" | "danger";
}

const TONE_CLASSES: Record<NonNullable<Row["tone"]>, string> = {
  default: "text-white",
  brand: "text-brand-500",
  warn: "text-warn-500",
  danger: "text-danger-500",
};

/**
 * The contextual panel above Logout — "Today's Summary" on Sales, "Stock
 * Overview" on Inventory.
 *
 * The stock figures are real, read from the ledger through the same endpoint the
 * Inventory screen uses — so the rail and the KPI cards can never disagree.
 *
 * Both halves are real now: stock from the ledger, takings from the sales
 * partition, each sharing its screen's own query key so the rail and the screen
 * cannot show different numbers.
 *
 * They still fall back to em dashes rather than zeroes while loading or when the
 * request fails. A zero is a claim — "you have sold nothing today" — and making
 * that claim because a request timed out is worse than admitting we do not know.
 */
export function SidebarSummary({ kind }: { kind: Kind }) {
  const { t } = useTranslation();

  // Only fetched on the Inventory rail, and sharing the Inventory screen's own
  // query key — so opening the screen does not fire a second identical request,
  // and a stock movement refreshes both at once.
  const activeSectionId = useAuthStore((s) => s.session?.activeSectionId ?? "all");

  const stock = useQuery({
    // Same key as the Inventory screen's, scope included — so the rail and the
    // KPI cards share one request and can never show different numbers.
    queryKey: ["inventory", "overview", activeSectionId],
    queryFn: () => inventoryRepository.overview(),
    enabled: kind === "stock",
    staleTime: 30_000,
  });

  // Same key as the Sales screen's, so the rail and the Recent Sales table are
  // one request and cannot disagree about the day's takings.
  const sales = useQuery({
    queryKey: ["sales", "summary", activeSectionId],
    queryFn: () => salesRepository.summary(),
    enabled: kind === "today",
    staleTime: 15_000,
  });

  const summary = stock.data?.summary;
  const today = sales.data?.today;

  const config: Record<Kind, { title: string; rows: Row[]; note: string }> = {
    today: {
      title: t("summary.today"),
      rows: [
        {
          label: t("summary.sales"),
          value: today ? formatMoney(today.sales_total) : null,
          tone: "brand",
        },
        { label: t("summary.transactions"), value: today ? String(today.transaction_count) : null },
        { label: t("summary.averageSale"), value: today ? formatMoney(today.average_sale) : null },
      ],
      note: today ? "" : t("summary.availableIn", { phase: "Phase 4" }),
    },
    stock: {
      title: t("summary.stockOverview"),
      rows: [
        {
          label: t("summary.totalProducts"),
          value: summary ? String(summary.total_products) : null,
        },
        {
          label: t("summary.totalStockValue"),
          // Undefined rather than null when the caller may not see cost prices —
          // the server omits the field entirely, so the row shows an em dash
          // rather than a misleading R 0.00.
          value: summary?.total_stock_value ? formatMoney(summary.total_stock_value) : null,
          tone: "brand",
        },
        {
          label: t("summary.lowStockItems"),
          value: summary ? String(summary.low_stock_items) : null,
          tone: "warn",
        },
        {
          label: t("summary.outOfStockItems"),
          value: summary ? String(summary.out_of_stock_items) : null,
          tone: "danger",
        },
      ],
      note: summary ? "" : t("summary.availableIn", { phase: "Phase 3" }),
    },
  };

  const { title, rows, note } = config[kind];

  return (
    // Compact by design. At the 1366×768 floor the rail must hold a logo, ten
    // 44px navigation rows, this panel and Logout — 44px being the accessibility
    // floor for a touch target, so the space has to come from here instead.
    <section className="rounded-card bg-navy-800 p-3.5" aria-label={title}>
      <h2 className="text-sm font-semibold text-white">{title}</h2>

      <dl className="mt-2.5 space-y-2">
        {rows.map((row) => (
          <div key={row.label} className="flex items-baseline justify-between gap-3">
            <dt className="truncate text-xs text-white/60">{row.label}</dt>
            <dd
              className={cn(
                "tabular shrink-0 text-sm font-semibold",
                row.value === null ? "text-white/30" : TONE_CLASSES[row.tone ?? "default"],
              )}
            >
              {row.value ?? "—"}
            </dd>
          </div>
        ))}
      </dl>

      {note && (
        <p className="mt-2.5 border-t border-white/10 pt-2 text-[0.6875rem] leading-snug text-white/40">
          {note}
        </p>
      )}
    </section>
  );
}
